import { NumberUtils } from "@/utils/NumberUtils";
import { Card } from "@tremor/react";
import React, { useState, useRef, useEffect } from "react";
import { BarChart as RechartsBarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Rectangle, Cell } from "recharts";
import { MultiLineTick } from "./StackBarChart";
import { IBarChartWidget } from "@/modules/widgets/types/BarChartWidget";
import classNames from "classnames";

const AngledTick = ({ x, y, payload, maxLength = 15 }: any) => {
  const text = payload.value || '';
  const truncated = text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={4}
        textAnchor="end"
        fill="#5a717a"
        fontSize={12}
        fontFamily="Roboto, sans-serif"
        transform="rotate(-45)"
      >
        <title>{text}</title>
        {truncated}
      </text>
    </g>
  );
};

interface CompareData {
  unified: {
    data: Array<{
      identifiers: {
        segment: string;
      };
      current?: Record<string, number>;
      previous?: Record<string, number>;
    }>;
  };
}

interface IBarChartCompareProps {
  rightTotalLegends?: boolean;
  data: IBarChartWidget['data'];
  compare: CompareData;
  colors?: string[];
  metadata?: IBarChartWidget['metadata'];
}

const HoverCursor = (props: any) => {
  const { x, y, width, height } = props;

  if (x == null || width == null || y == null || height == null) return null;

  return (
    <Rectangle
      x={x}
      y={y}
      width={width}
      height={height}
      fill="rgba(148, 163, 184, 0.18)" 
      radius={4}
    />
  );
};

const adjustColorBrightness = (hex: string, percent: number): string => {
  const num = parseInt(hex.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  return "#" + (
    0x1000000 +
    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
    (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
    (B < 255 ? (B < 1 ? 0 : B) : 255)
  ).toString(16).slice(1);
};

const BarChartCompare: React.FC<IBarChartCompareProps> = ({ rightTotalLegends = false, data, compare, colors, metadata }) => {

  const [hiddenMetrics, setHiddenMetrics] = useState(new Set<string>());
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const defaultColors = ['#341cac', '#429795', '#84c4c4', '#6b46c1'];
  const chartColors = colors && colors.length > 0 ? colors : defaultColors;

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  if (data.error) {
    return <div className="py-5 px-10 text-center text-red-500">Error: {data.error}</div>
  }

  const dataPointCount = compare.unified.data?.length || 0;
  const shouldAngleLabels = containerWidth > 0 && (
    (containerWidth < 600 && dataPointCount > 4) ||
    (containerWidth < 800 && dataPointCount > 6) ||
    (dataPointCount > 8)
  );

  const toggleMetric = (metricName: string) => {
    setHiddenMetrics((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(metricName)) {
        newSet.delete(metricName);
      } else {
        newSet.add(metricName);
      }
      return newSet;
    });
  }

  const metrics = data.metrics.filter(metric => !hiddenMetrics.has(metric.metricName)).sort((a, b) => {
    return data.metrics.findIndex(metric => metric.metricName === a.metricName) - data.metrics.findIndex(metric => metric.metricName === b.metricName)
  })

  const hasAnyNonZeroValue = compare.unified.data.some(item =>
    metrics.some(metric => {
      const currentValue = Number(item.current?.[metric.metricName]) || 0;
      const previousValue = Number(item.previous?.[metric.metricName]) || 0;
      return currentValue !== 0 || previousValue !== 0;
    })
  );

  const transformedData: any[] = [];

  compare.unified.data.forEach((item, segmentIndex) => {
    const hasData = metrics.some(metric => {
      const currentValue = Number(item.current?.[metric.metricName]) || 0;
      const previousValue = Number(item.previous?.[metric.metricName]) || 0;
      return currentValue !== 0 || previousValue !== 0;
    });

    if (!metadata?.showZeroBars || !hasAnyNonZeroValue) {
      if (!hasData) return;
    }

    metrics.forEach((metric, metricIndex) => {
      const currentValue = item.current?.[metric.metricName] || 0;
      const previousValue = item.previous?.[metric.metricName] || 0;

      transformedData.push({
        xAxisKey: `${item.identifiers.segment}_${metricIndex}`,
        segment: item.identifiers.segment,
        metricName: metric.metricName,
        metricShowName: metric.showName,
        metricIndex,
        current: currentValue,
        previous: previousValue,
        isSpacer: false
      });
    });
  });

  const getTotalMetric = (metricName: string, period: 'current' | 'previous') => {
    const values = transformedData
      .filter(item => item.metricName === metricName)
      .map(item => item[period] as number);
    const total = values.reduce((a, b) => a + b, 0);
    return NumberUtils.formatCompact(total)
  };

  const getColorForMetric = (metricName: string, period: 'current' | 'previous') => {
    if (!metricName) return 'transparent'; // Handle spacer entries
    const metricIndex = data.metrics.findIndex(m => m.metricName === metricName);
    if (metricIndex === -1) return 'transparent';
    const baseColor = chartColors[metricIndex % chartColors.length];
    return period === 'previous' ? adjustColorBrightness(baseColor, -30) : baseColor;
  };

  return (
    <Card ref={containerRef} className="w-full mx-auto p-6 flex flex-col justify-between">
      {!rightTotalLegends && (
        <ul className="flex justify-start gap-5 text-primary-blue list-none p-0 mb-4">
          {data.metrics.map((metric, i) => (
            <li key={metric.metricName} onClick={() => toggleMetric(metric.metricName)} className={classNames("flex gap-2 items-center font-normal cursor-pointer", hiddenMetrics.has(metric.metricName) ? "opacity-50" : "")}>
              <span
                className="w-3 h-3 rounded-[3px] inline-block"
                style={{
                  backgroundColor: chartColors[i % chartColors.length],
                }}
              />
              <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px' }}>{metric.showName}</span>
            </li>
          ))}
        </ul>
      )}

      <ResponsiveContainer width="100%" height={400}>
        <RechartsBarChart
          data={transformedData}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          barGap={0}
          barCategoryGap="15%"
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={true}
            horizontal={true}
            stroke="#e5e7eb"
          />
          <XAxis
            dataKey="xAxisKey"
            interval={0}
            height={shouldAngleLabels ? 80 : 60}
            tick={(props) => {
              const dataPoint = transformedData.find(d => d.xAxisKey === props.payload.value);
              if (!dataPoint) return null;
              
              // Only show label for the middle metric of each segment to center it
              const middleIndex = Math.floor(metrics.length / 2);
              if (dataPoint.metricIndex !== middleIndex) {
                return null;
              }
              
              const label = dataPoint.segment;
              
              if (shouldAngleLabels) {
                return <AngledTick {...props} payload={{ value: label }} />;
              }
              return (
                <MultiLineTick
                  {...props}
                  payload={{ value: label }}
                  maxCharsPerLine={12}
                  maxLines={2}
                  fontSize={12}
                  lineHeight={14}
                />
              );
            }}
            axisLine={{ stroke: '#e5e7eb' }}
            tickLine={{ stroke: '#e5e7eb' }}
          />

          <YAxis
            tick={{ fill: '#5a717a', fontSize: 12, fontFamily: 'Roboto, sans-serif', opacity: 1 }}
            axisLine={{ stroke: '#e5e7eb' }}
            tickLine={{ stroke: '#e5e7eb' }}
            tickFormatter={NumberUtils.formatCompact}
          />

          <Tooltip
            cursor={<HoverCursor />}
            content={({ active, payload }) => {
              if (!active || !payload || !payload.length) return null;

              const dataPoint = payload[0].payload;
              
              // Skip spacer entries
              if (dataPoint.isSpacer) return null;

              const segment = dataPoint.segment;
              const metricShowName = dataPoint.metricShowName;
              const currentValue = dataPoint.current;
              const previousValue = dataPoint.previous;
              const metricName = dataPoint.metricName;

              return (
                <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4">
                  <div className="font-normal mb-2">
                    {segment}
                  </div>

                  <div className="mb-0">
                    <div className="font-medium text-sm mb-1">{metricShowName}</div>
                    
                    <div className="flex items-center gap-2 text-sm mt-1">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{
                            backgroundColor: getColorForMetric(metricName, 'current'),
                            flexShrink: 0
                          }}
                        />
                        Current:
                      </div>
                      <div>
                        {NumberUtils.formatStandard(NumberUtils.round(currentValue))}
                      </div>
                    </div>

                    {previousValue !== undefined && previousValue !== null && (
                      <div className="flex items-center gap-2 text-sm mt-1">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{
                              backgroundColor: getColorForMetric(metricName, 'previous'),
                              flexShrink: 0
                            }}
                          />
                          Previous:
                        </div>
                        <div>
                          {NumberUtils.formatStandard(NumberUtils.round(previousValue))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            }}
          />

          <Bar dataKey="current" radius={[6, 6, 0, 0]} barSize={40}>
            {transformedData.map((entry, index) => (
              <Cell key={`cell-current-${index}`} fill={getColorForMetric(entry.metricName, 'current')} />
            ))}
          </Bar>
          <Bar dataKey="previous" radius={[6, 6, 0, 0]} barSize={40}>
            {transformedData.map((entry, index) => (
              <Cell key={`cell-previous-${index}`} fill={getColorForMetric(entry.metricName, 'previous')} />
            ))}
          </Bar>
        </RechartsBarChart>
      </ResponsiveContainer>

      {data.metrics && rightTotalLegends && (
        <div className="mt-6 lg:mt-0 lg:w-[180px] lg:flex-shrink-0 overflow-hidden">
          <ul
            role="list"
            className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1 max-h-96 overflow-y-auto"
          >
            {data.metrics.map((metric, index) => {
              const color = chartColors[index % chartColors.length];

              return (
                <li
                  key={metric.metricName}
                  className="rounded-tremor-small border border-gray-300 rounded px-3 py-2 text-left dark:border-dark-tremor-border w-full max-w-11/12"
                  style={{ height: '78px' }}
                >
                  <div className="flex items-center space-x-1.5">
                    {data.metrics.length > 1 && (
                      <span
                        className="size-2.5 rounded-sm"
                        style={{ backgroundColor: color }}
                        aria-hidden={true}
                      />
                    )}
                    <p
                      className="truncate"
                      style={{
                        color: '#2f363f',
                        fontSize: '12px',
                        fontFamily: 'Roboto, sans-serif',
                        maxWidth: data.metrics.length > 1 ? '100px' : '120px',
                      }}
                      title={metric.showName}
                    >
                      {metric.showName}
                    </p>
                  </div>
                  <div className="mt-1">
                    <p
                      className="text-xs"
                      style={{
                        color: '#6b7280',
                        fontFamily: 'Roboto, sans-serif'
                      }}
                    >
                      Current: <span className="font-semibold text-gray-900">{getTotalMetric(metric.metricName, 'current')}</span>
                    </p>
                    <p
                      className="text-xs mt-0.5"
                      style={{
                        color: '#6b7280',
                        fontFamily: 'Roboto, sans-serif'
                      }}
                    >
                      Previous: <span className="font-semibold text-gray-900">{getTotalMetric(metric.metricName, 'previous')}</span>
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Card>
  );
}

export { BarChartCompare }
